/**
 * Flex App Bridge — communication layer between web and native shell.
 *
 * When running inside the Flex app WebView, the native shell injects:
 *   - window.isFlexApp = true
 *   - window.ReactNativeWebView.postMessage (for web → native)
 *   - window.postMessage (for native → web, via injectJavaScript)
 *
 * This module provides typed helpers for both directions.
 */

export enum FLEX_BRIDGE_EVENTS {
	// web → native
	LOGIN_CLICKED = "LOGIN_CLICKED",
	LOGOUT_SUCCESS = "LOGOUT_SUCCESS",
	OPEN_EXTERNAL_URL = "OPEN_EXTERNAL_URL",
	REQUEST_FILE_DOWNLOAD = "REQUEST_FILE_DOWNLOAD",

	// native → web
	USER_AUTH_TOKEN = "USER_AUTH_TOKEN",
	DEEPLINK = "DEEPLINK",
	BACK_ACTION_TRIGGERED = "BACK_ACTION_TRIGGERED",

	// injected by the native shell (web does not send these)
	LOG = "log",
	WARN = "warn",
	ERROR = "error",
}

interface BridgeMessage {
	type: string;
	payload?: unknown;
}

/**
 * Send an event from web to the native Flex app shell.
 * No-ops if not running inside the Flex app.
 */
export function sendToFlexApp(
	type: FLEX_BRIDGE_EVENTS,
	payload?: unknown,
): void {
	if (typeof window === "undefined" || !window.isFlexApp) return;
	if (!window.ReactNativeWebView?.postMessage) return;

	const message: BridgeMessage = { type, payload };
	window.ReactNativeWebView.postMessage(JSON.stringify(message));
}

type FlexEventHandler = (payload?: unknown) => void;
const handlers: Map<string, FlexEventHandler[]> = new Map();

/**
 * Subscribe to events from the native Flex app shell.
 * Returns an unsubscribe function.
 */
export function onFlexAppEvent<T = unknown>(
	type: FLEX_BRIDGE_EVENTS,
	handler: (payload: T) => void,
): () => void {
	if (!handlers.has(type)) {
		handlers.set(type, []);
	}
	const list = handlers.get(type);
	list?.push(handler as FlexEventHandler);

	return () => {
		const list = handlers.get(type);
		if (list) {
			handlers.set(
				type,
				list.filter((h) => h !== handler),
			);
		}
	};
}

/**
 * Initialize the Flex bridge event listener.
 * Call once at app startup (only activates inside the Flex app).
 */
export function initFlexBridge(): void {
	if (typeof window === "undefined" || !window.isFlexApp) return;

	window.addEventListener("message", (event: MessageEvent) => {
		try {
			const data: BridgeMessage =
				typeof event.data === "string" ? JSON.parse(event.data) : event.data;

			if (data && typeof data === "object" && "type" in data) {
				const list = handlers.get(data.type) || [];
				for (const handler of list) {
					handler(data.payload);
				}
			}
		} catch {
			// ignore malformed messages
		}
	});
}

// TypeScript declarations for native bridge
declare global {
	interface Window {
		isFlexApp?: boolean;
		ReactNativeWebView?: {
			postMessage: (message: string) => void;
		};
	}
}
